import { useEffect, useRef } from "preact/hooks";

import {
  activeJobId,
  allJobs,
  config,
  filters,
  isScanning,
  scanErrors,
  scanHistory,
  scanStatus,
  showToast,
} from "../store/appStore";
import {
  fetchConfig,
  fetchJobs,
  fetchScanErrors,
  fetchScanHistory,
  fetchScanStatus,
  triggerScan,
} from "../lib/api";
import type { Job } from "../lib/types";

/** Loads and refreshes the jobs list. Re-runs whenever the search query,
 * timeframe, or active scan run changes (server-side filters). */
export function useJobs() {
  const lastKey = useRef<string>("");

  useEffect(() => {
    let cancelled = false;

    const loadJobs = () => {
      const f = filters.value;
      const key = `${f.timeframe}|${f.q}|${f.runId ?? ""}`;
      if (key === lastKey.current) return;
      lastKey.current = key;

      (async () => {
        try {
          const jobs = await fetchJobs({
            filter: f.timeframe,
            q: f.q,
            runId: f.runId,
          });
          if (cancelled) return;
          // Detect newly added jobs since last fetch (for browser notifications).
          const prev = allJobs.value;
          if (prev.length > 0) {
            const existing = new Set(prev.map((j) => j.id));
            const fresh = jobs.filter((j: Job) => !existing.has(j.id));
            if (fresh.length > 0) maybeNotify(fresh);
          }
          allJobs.value = jobs;
        } catch (err) {
          console.error("useJobs fetch error", err);
        }
      })();
    };

    loadJobs();
    const unsubscribe = filters.subscribe(loadJobs);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);
}

function maybeNotify(newJobs: Job[]) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  if (newJobs.length === 1) {
    const j = newJobs[0];
    new Notification(`New job: ${j.title} at ${j.company}`, { body: j.location });
  } else {
    new Notification(`${newJobs.length} new ML jobs found`, {
      body: "Check your ML Job Tracker dashboard.",
    });
  }
}

/** Loads config (keywords, watchlist, profiles) once on mount. */
export function useConfig() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await fetchConfig();
        if (!cancelled) config.value = c;
      } catch (err) {
        console.error("useConfig fetch error", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}

/** Polls /api/scan-status every 2s while a scan is running, and every
 * 10s otherwise (for the next-scan countdown). Also loads scan history
 * and scan errors once. Exposes a startScan() action. */
export function useScanStatus() {
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    const schedule = (delayMs: number) => {
      if (cancelled) return;
      timer = window.setTimeout(() => void poll(), delayMs);
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const s = await fetchScanStatus();
        const wasRunning = isScanning.value;
        scanStatus.value = s;
        const running = s.status === "running";
        isScanning.value = running;

        if (wasRunning && !running) {
          showToast("Scan complete");
          refreshScanErrors();
          void refreshJobs();
        }

        schedule(running ? 2000 : 10000);
      } catch (err) {
        console.error("useScanStatus poll error", err);
        schedule(10000);
      }
    };

    poll();

    // One-shot loads.
    (async () => {
      try {
        const [history, errors] = await Promise.all([
          fetchScanHistory(),
          fetchScanErrors(),
        ]);
        scanHistory.value = history;
        scanErrors.value = errors;
      } catch (err) {
        console.error("useScanStatus one-shot error", err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  return async function startScan() {
    if (isScanning.value) {
      showToast("Scan already in progress");
      return "already_running" as const;
    }
    try {
      const res = await triggerScan();
      isScanning.value = true;
      if (res.status === "already_running") {
        showToast("Scan already in progress");
        return "already_running" as const;
      }
      showToast("Scan started…");
      return "started" as const;
    } catch (err) {
      isScanning.value = false;
      console.error("startScan error", err);
      showToast("Failed to start scan");
      return "failed" as const;
    }
  };
}

/** Reload jobs with the current filters (e.g. after a scan completes). */
export async function refreshJobs(): Promise<void> {
  const f = filters.value;
  try {
    const jobs = await fetchJobs({
      filter: f.timeframe,
      q: f.q,
      runId: f.runId,
    });
    allJobs.value = jobs;
  } catch (err) {
    console.error("refreshJobs error", err);
  }
}

/** Refreshes scan errors after a scan completes or after an AI Fix. */
export function refreshScanErrors() {
  (async () => {
    try {
      scanErrors.value = await fetchScanErrors();
      scanHistory.value = await fetchScanHistory();
    } catch (err) {
      console.error("refreshScanErrors error", err);
    }
  })();
}

/** Keeps activeJobId in sync with the URL ?job= param. The actual URL
 * read/write happens in useUrlState (phase 7); this hook just clears
 * the active job when the jobs list no longer contains it. */
export function useActiveJobGuard() {
  useEffect(() => {
    const id = activeJobId.value;
    if (!id) return;
    if (!allJobs.value.some((j) => j.id === id)) {
      activeJobId.value = null;
    }
  }, [allJobs.value]);
}
